import React, { useState, useRef } from "react";
import SummaryApi from "../common";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { CheckCircle, XCircle } from "lucide-react";

const AdminPasswordReset = () => {
  const [passwords, setPasswords] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);

  const ADMIN_TOAST_ID = "admin-password-toast";
  const toastLock = useRef(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPasswords((prev) => ({ ...prev, [name]: value }));
  };

  // --- Custom Toast Logic ---
  const showSuccessToast = (msg) => {
    if (toastLock.current) return;
    toastLock.current = true;

    toast.custom(
      () => (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          className="pointer-events-auto max-w-md w-full bg-white shadow-2xl rounded-[1.5rem] flex border-b-4 border-rose-500"
        >
          <div className="flex-1 p-4">
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 rounded-xl bg-rose-100 flex items-center justify-center text-rose-500">
                <CheckCircle size={28} />
              </div>
              <div>
                <p className="text-sm font-bold text-rose-950">Success!</p>
                <p className="text-xs text-rose-900/60 mt-1">{msg}</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => toast.dismiss(ADMIN_TOAST_ID)}
            className="px-4 font-bold text-rose-600"
          >
            Close
          </button>
        </motion.div>
      ),
      {
        id: ADMIN_TOAST_ID,
        duration: 2000,
        onClose: () => {
          toastLock.current = false;
        },
      }
    );
  };

  const showErrorToast = (msg) => {
    if (toastLock.current) return;
    toastLock.current = true;

    toast.custom(
      () => (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          className="pointer-events-auto max-w-md w-full bg-white shadow-2xl rounded-[1.5rem] flex border-b-4 border-red-500"
        >
          <div className="flex-1 p-4">
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center text-red-500">
                <XCircle size={28} />
              </div>
              <div>
                <p className="text-sm font-bold text-red-950">Update Failed</p>
                <p className="text-xs text-red-900/60 mt-1">{msg}</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => toast.dismiss(ADMIN_TOAST_ID)}
            className="px-4 font-bold text-red-600"
          >
            Close
          </button>
        </motion.div>
      ),
      {
        id: ADMIN_TOAST_ID,
        duration: 2000,
        onClose: () => {
          toastLock.current = false;
        },
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (passwords.newPassword !== passwords.confirmPassword) {
      showErrorToast("Passwords do not match!");
      return;
    }

    const encodedId = sessionStorage.getItem("H7Z4L9VD2XK5JW8QRYT6");
    if (!encodedId) {
      showErrorToast("Session expired. Please login again.");
      return;
    }

    const adminId = atob(encodedId);

    setLoading(true);
    try {
      const response = await fetch(SummaryApi.AdminChangePassword.url, {
        method: SummaryApi.AdminChangePassword.method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          admin_id: adminId,
          password: passwords.newPassword,
        }),
      });

      const dataResponse = await response.json();

      if (dataResponse.status) {
        showSuccessToast(dataResponse.message || "Password updated successfully!");
        setPasswords({ newPassword: "", confirmPassword: "" });
      } else {
        showErrorToast(dataResponse.message || "Failed to update password.");
      }
    } catch (error) {
      console.error("Update error", error);
      showErrorToast("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen p-4 md:p-6 bg-gray-50">
      <div className="w-full min-h-[80vh] bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-12">
        <div className="border-b border-gray-100 pb-4 mb-8">
          <h2 className="text-2xl font-bold text-gray-800">Change Admin Password</h2>
          <p className="text-gray-500 mt-1">Update your account security settings here.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 max-w-md">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              New Password
            </label>
            <input
              type="password"
              name="newPassword"
              value={passwords.newPassword}
              onChange={handleChange}
              placeholder="Enter new password"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Confirm Password
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={passwords.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm new password"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
              required
              disabled={loading}
            />
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={loading}
              className={`w-full md:w-auto px-8 py-3 bg-[#e91e63] hover:bg-[#d81b60] text-white font-bold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 active:scale-95 flex items-center justify-center gap-2 ${
                loading ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                "Change Password"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminPasswordReset;